// _shared/hooks/useLive.js
import { useState, useEffect, useRef } from 'react';
import { liveService } from '../../_services/liveService';

export const useLive = (slug) => {
  // Estados de datos
  const [resultados, setResultados] = useState([]);
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [categoriasConfiguradas, setCategoriasConfiguradas] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [vistaAdmin, setVistaAdmin] = useState(false);

  // Obtener rol y token
  const userRole = liveService.getUserRole();
  const token = liveService.getToken();
  const isAdmin = liveService.isAdmin(userRole);

  // Activar vista admin si es admin
  useEffect(() => {
    if (isAdmin) {
      setVistaAdmin(true);
    }
  }, [isAdmin]);

  // Cargar resultados al montar
  useEffect(() => {
    if (slug) {
      fetchResultados();
    }
  }, [slug]);

  /**
   * Obtener resultados
   */
  const fetchResultados = async () => {
    try {
      const data = await liveService.getResultados(slug);
      // Si la data es null o undefined, aseguramos que sea un array vacío
      setResultados(data || []);
    } catch (err) {
      // ⚠️ Usamos console.warn en lugar de error para no ensuciar la consola en producción
      console.warn("⚠️ [useLive] Tiempos no disponibles aún o error de red:", err.message);
      
      // ✅ CLAVE: Establecemos un array vacío para que el frontend no se rompa
      setResultados([]); 
      
      // Opcional: Si quieres que el admin lo sepa, descomenta la siguiente línea:
      // mostrarAlerta('error', 'No se pudieron cargar los tiempos oficiales');
    }
  };

  /**
   * Mostrar alerta
   */
  const mostrarAlerta = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: '', texto: '' }), 5000);
  };

  /**
   * Agregar categoría localmente
   */
  const handleAddCategoriaLocal = (e) => {
    e.preventDefault();
    if (!nuevaCategoria.trim()) return;
    
    const categoriaUpper = nuevaCategoria.trim().toUpperCase();
    if (categoriasConfiguradas.includes(categoriaUpper)) {
      return mostrarAlerta('error', 'Esta categoría ya fue agregada a la lista.');
    }
    
    setCategoriasConfiguradas([...categoriasConfiguradas, categoriaUpper]);
    setNuevaCategoria('');
  };

  /**
   * Eliminar categoría localmente
   */
  const handleRemoveCategoriaLocal = (index) => {
    setCategoriasConfiguradas(categoriasConfiguradas.filter((_, i) => i !== index));
  };

  /**
   * Guardar categorías en la base de datos
   */
  const handleGuardarCategoriasBD = async () => {
    if (categoriasConfiguradas.length === 0) {
      return mostrarAlerta('error', 'Debes agregar al menos una categoría a la lista.');
    }

    setLoading(true);
    try {
      await liveService.saveCategorias(slug, categoriasConfiguradas, token);
      mostrarAlerta('success', 'Configuración de categorías guardada correctamente.');
    } catch (err) {
      mostrarAlerta('error', err.response?.data?.error || 'Error al guardar categorías.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Subir PDF de resultados
   */
  const handleUploadPdf = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      return mostrarAlerta('error', 'Por favor selecciona un archivo PDF válido.');
    }

    setLoading(true);
    try {
      const res = await liveService.uploadPdf(slug, pdfFile, token);
      mostrarAlerta('success', res.message || 'Resultados publicados con éxito.');
      setPdfFile(null);
      await fetchResultados();
      setVistaAdmin(false);
    } catch (err) {
      mostrarAlerta('error', err.response?.data?.error || 'Error al procesar el PDF.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Alternar vista admin
   */
  const toggleVistaAdmin = () => {
    setVistaAdmin(!vistaAdmin);
  };

  return {
    // Estado
    resultados,
    nuevaCategoria,
    categoriasConfiguradas,
    pdfFile,
    loading,
    mensaje,
    vistaAdmin,
    isAdmin,

    // Setters
    setNuevaCategoria,
    setCategoriasConfiguradas,
    setPdfFile,
    setMensaje,
    setVistaAdmin,

    // Handlers
    fetchResultados,
    handleAddCategoriaLocal,
    handleRemoveCategoriaLocal,
    handleGuardarCategoriasBD,
    handleUploadPdf,
    toggleVistaAdmin,
    mostrarAlerta,
  };
};